import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Slider,
    Button,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import { panelTheme } from '../PanelComerciales.theme';
import {
    DistributionAxisDef,
    FlowTypeDef,
    ProductDef,
    sumSpec,
    productColor,
    flowTypeColor,
} from '../PanelComerciales.types';
import { balanceToHundred } from '../utils/specialty';
import { SpecialtyBar } from '../PanelComerciales.styles';

/** Emisión del editor: ambos ejes por separado. */
export interface DistributionValue {
    products: Record<string, number>;
    flows: Record<string, number>;
}

interface SpecialtyEditorProps {
    /** EJE PRODUCTO: valores actuales (key = product_code). */
    value: Record<string, number>;
    /** EJE FLUJO: valores actuales (key = pipeline_key). */
    flowsValue?: Record<string, number>;
    /** Catálogo de productos (key = product_code). */
    products?: ProductDef[];
    /** Catálogo de tipos de flujo (key = pipeline_key). */
    flowTypes?: FlowTypeDef[];
    /**
     * Emite los DOS ejes por separado. Compat: si un caller legacy aún espera
     * sólo el eje producto, lee `next.products`.
     */
    onChange: (next: DistributionValue) => void;
}

const ModalBody = styled.div`
    width: 420px;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;

    .row {
        display: flex;
        align-items: center;
        gap: 12px;

        .swatch {
            flex: 0 0 8px;
            width: 8px;
            height: 8px;
            border-radius: 50%;
        }
        .label {
            flex: 0 0 120px;
            width: 120px;
            max-width: 120px;
            font-size: 12px;
            color: ${panelTheme.colors.textMuted};
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .MuiSlider-root {
            flex: 1 1 auto;
            min-width: 120px;
        }
        .pct {
            flex: 0 0 36px;
            min-width: 36px;
            text-align: right;
            font-weight: 600;
            color: ${panelTheme.colors.text};
        }
    }

    .total {
        font-size: 12px;
        font-weight: 600;
        margin-top: 4px;
    }
    .total.invalid {
        color: ${panelTheme.colors.danger};
    }
    .total.valid {
        color: ${panelTheme.colors.success};
    }
    .empty {
        font-size: 12px;
        color: ${panelTheme.colors.textMuted};
        padding: 16px 0;
    }
`;

/** Una fila de slider de un eje. */
const AxisSlider: React.FC<{
    item: DistributionAxisDef;
    pct: number;
    color: string;
    onChange: (key: string, v: number) => void;
}> = ({ item, pct, color, onChange }) => (
    <div className='row'>
        <span className='swatch' style={{ background: color }} />
        <span className='label' title={item.label}>{item.label}</span>
        <Slider
            size='small'
            value={pct}
            min={0}
            max={100}
            step={5}
            onChange={(_, v) => onChange(item.key, Array.isArray(v) ? v[0] : v)}
            sx={{ color, flex: 1 }}
        />
        <span className='pct'>{pct}%</span>
    </div>
);

/** Panel de un eje: catálogo + draft + total. Reusa balanceToHundred al editar. */
const AxisPanel: React.FC<{
    catalog: DistributionAxisDef[];
    draft: Record<string, number>;
    colorFn: (index: number) => string;
    emptyText: string;
    onSet: (key: string, v: number) => void;
}> = ({ catalog, draft, colorFn, emptyText, onSet }) => {
    if (catalog.length === 0) {
        return <div className='empty'>{emptyText}</div>;
    }
    const total = sumSpec(draft);
    const valid = total === 100;
    return (
        <>
            {catalog.map((item, i) => (
                <AxisSlider
                    key={item.key}
                    item={item}
                    pct={draft[item.key] ?? 0}
                    color={colorFn(i)}
                    onChange={onSet}
                />
            ))}
            <span className={`total ${valid ? 'valid' : 'invalid'}`}>
                Total: {total}% {valid ? '' : '(debe ser 100)'}
            </span>
        </>
    );
};

/** Limita 0..100 y rebalancea el resto del eje a 100 sobre la clave pivote. */
const editAxis = (
    prev: Record<string, number>,
    key: string,
    v: number,
): Record<string, number> => {
    const clamped = Math.max(0, Math.min(100, v));
    return balanceToHundred({ ...prev, [key]: clamped }, key);
};

/**
 * Restringe un dict de porcentajes a las claves del catálogo VISIBLE. Purga
 * porcentajes legacy de productos retirados del catálogo (ej. ANUNCIO_SIMPLE
 * / SMART_LEADS, decisión 2026-07): sin esto, el total sumaría claves ocultas
 * y el editor quedaría bloqueado en "debe ser 100" sin filas que ajustar.
 */
const restrictToCatalog = (
    spec: Record<string, number>,
    catalog: DistributionAxisDef[],
): Record<string, number> => {
    const allowed = new Set(catalog.map((item) => item.key));
    return Object.fromEntries(
        Object.entries(spec).filter(([key]) => allowed.has(key)),
    );
};

type AxisTab = 'products' | 'flows';

const SpecialtyEditor: React.FC<SpecialtyEditorProps> = ({
    value,
    flowsValue,
    products,
    flowTypes,
    onChange,
}) => {
    const productCatalog: ProductDef[] = useMemo(() => products ?? [], [products]);
    const flowCatalog: FlowTypeDef[] = useMemo(() => flowTypes ?? [], [flowTypes]);

    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState<AxisTab>('products');
    // Drafts SIEMPRE restringidos al catálogo visible: los % legacy de claves
    // retiradas no cuentan para el total ni viajan en el PUT (el backend los
    // rechazaría como claves inválidas).
    const [productDraft, setProductDraft] = useState<Record<string, number>>(
        () => restrictToCatalog(value, productCatalog),
    );
    const [flowDraft, setFlowDraft] = useState<Record<string, number>>(
        () => restrictToCatalog(flowsValue ?? {}, flowCatalog),
    );

    const openModal = () => {
        setProductDraft(restrictToCatalog(value, productCatalog));
        setFlowDraft(restrictToCatalog(flowsValue ?? {}, flowCatalog));
        setTab(productCatalog.length ? 'products' : 'flows');
        setOpen(true);
    };
    const close = () => setOpen(false);

    // Cada eje PRESENTE debe sumar 100 de forma independiente. Un eje sin
    // catálogo no bloquea el guardado.
    const productValid = productCatalog.length === 0 || sumSpec(productDraft) === 100;
    const flowValid = flowCatalog.length === 0 || sumSpec(flowDraft) === 100;
    const canApply = productValid && flowValid;

    const setProductKey = (k: string, v: number) =>
        setProductDraft((prev) => editAxis(prev, k, v));
    const setFlowKey = (k: string, v: number) =>
        setFlowDraft((prev) => editAxis(prev, k, v));

    const apply = () => {
        if (!canApply) return;
        onChange({ products: productDraft, flows: flowDraft });
        close();
    };

    // Las barras del trigger: producto arriba, flujo abajo (si existe el eje).
    const productBar = useMemo(
        () =>
            productCatalog.map((p, i) => {
                const pct = value[p.key] || 0;
                if (pct <= 0) return null;
                return (
                    <span key={p.key} style={{ width: `${pct}%`, background: productColor(i) }} />
                );
            }),
        [productCatalog, value],
    );
    const flowBar = useMemo(
        () =>
            flowCatalog.map((f, i) => {
                const pct = (flowsValue ?? {})[f.key] || 0;
                if (pct <= 0) return null;
                return (
                    <span key={f.key} style={{ width: `${pct}%`, background: flowTypeColor(i) }} />
                );
            }),
        [flowCatalog, flowsValue],
    );

    return (
        <>
            <span
                onClick={openModal}
                style={{ cursor: 'pointer', display: 'inline-flex', flexDirection: 'column', gap: 3 }}
            >
                <SpecialtyBar>{productBar}</SpecialtyBar>
                {flowCatalog.length > 0 && <SpecialtyBar>{flowBar}</SpecialtyBar>}
            </span>
            <Dialog
                open={open}
                onClose={close}
                PaperProps={{ sx: { borderRadius: panelTheme.radius.md } }}
            >
                <DialogTitle sx={{ pb: 0 }}>
                    <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                        Distribución del comercial
                    </Typography>
                    <Typography variant='caption' sx={{ color: panelTheme.colors.textMuted }}>
                        Cada eje debe sumar 100% de forma independiente.
                    </Typography>
                </DialogTitle>
                <Tabs
                    value={tab}
                    onChange={(_, v: AxisTab) => setTab(v)}
                    sx={{ px: 3, minHeight: 40 }}
                >
                    <Tab
                        value='products'
                        label='Por producto'
                        disabled={productCatalog.length === 0}
                        sx={{ textTransform: 'none', minHeight: 40 }}
                    />
                    <Tab
                        value='flows'
                        label='Por tipo de flujo'
                        disabled={flowCatalog.length === 0}
                        sx={{ textTransform: 'none', minHeight: 40 }}
                    />
                </Tabs>
                <DialogContent>
                    <ModalBody>
                        {tab === 'products' ? (
                            <AxisPanel
                                catalog={productCatalog}
                                draft={productDraft}
                                colorFn={productColor}
                                emptyText='Sin catálogo de productos.'
                                onSet={setProductKey}
                            />
                        ) : (
                            <AxisPanel
                                catalog={flowCatalog}
                                draft={flowDraft}
                                colorFn={flowTypeColor}
                                emptyText='Sin catálogo de tipos de flujo.'
                                onSet={setFlowKey}
                            />
                        )}
                    </ModalBody>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button size='small' onClick={close} sx={{ textTransform: 'none' }}>
                        Cancelar
                    </Button>
                    <Button
                        size='small'
                        variant='contained'
                        disabled={!canApply}
                        onClick={apply}
                        sx={{
                            textTransform: 'none',
                            background: panelTheme.colors.indigo,
                            '&:hover': { background: panelTheme.colors.violet },
                        }}
                    >
                        Aplicar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default SpecialtyEditor;
